import {
  IconCheck,
  IconChevronRight,
  IconCopy,
  IconMail,
  IconMessageCircle,
  IconPhone,
  IconX,
} from "@tabler/icons-react";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import useFocusTrap from "@/hooks/useFocusTrap";
import useScrollLock from "@/hooks/useScrollLock";
import {useLogo} from "@/util/logoUtils";
import styles from "./SupportModal.module.css";

const MODAL_TITLE_ID = "support-modal-title";

const SupportModal = ({open, onClose}) => {
  const modalRef = useRef(null);
  const logo = useLogo();
  const [copied, setCopied] = useState(false);
  useFocusTrap(modalRef, open);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("soporte@aprietaygana.com");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={MODAL_TITLE_ID}
    >
      <div
        className={styles.modal}
        ref={modalRef}
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Cerrar"
        >
          <IconX size={18} />
        </button>

        <div className={styles.header}>
          <img
            src={logo}
            alt="Aprieta y Gana"
            className={styles.logo}
          />
          <h2
            className={styles.title}
            id={MODAL_TITLE_ID}
          >
            ¿Necesitas ayuda?
          </h2>
          <p className={styles.subtitle}>¿Alguna duda? Escríbenos por WhatsApp o correo. Estamos para ayudarte.</p>
        </div>

        <div className={styles.contactList}>
          <button
            type="button"
            onClick={handleCopyEmail}
            className={styles.contactRow}
          >
            <div className={`${styles.contactIcon} ${styles.iconEmail}`}>
              <IconMail size={18} />
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactLabel}>Correo electrónico</span>
              <span className={styles.contactValue}>soporte@aprietaygana.com</span>
            </div>
            {copied ? (
              <IconCheck
                size={16}
                className={styles.contactCopied}
              />
            ) : (
              <IconCopy
                size={16}
                className={styles.contactChevron}
              />
            )}
          </button>

          <a
            href="https://t.me/raspaygana_bot"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.contactRow}
          >
            <div className={`${styles.contactIcon} ${styles.iconTelegram}`}>
              <IconMessageCircle size={18} />
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactLabel}>Telegram</span>
              <span className={styles.contactValue}>t.me/raspaygana_bot</span>
            </div>
            <IconChevronRight
              size={16}
              className={styles.contactChevron}
            />
          </a>

          <a
            href="https://wa.me/584127230400"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.contactRow}
          >
            <div className={`${styles.contactIcon} ${styles.iconWhatsApp}`}>
              <IconPhone size={18} />
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactLabel}>WhatsApp</span>
              <span className={styles.contactValue}>+58 412 723 0400</span>
            </div>
            <IconChevronRight
              size={16}
              className={styles.contactChevron}
            />
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SupportModal;
